import { type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { useMe } from '@/api/hooks';
import ErrorBoundary, { ErrorBoundaryFallback } from '@/components/ErrorBoundary';
import PageLoader from '@/components/Loaders/PageLoader';
import PartnerTemplate from '@/components/template/PartnerTemplate';
import Account from '@/domain/account/Account';
import AccountEdit from '@/domain/account/AccountEdit';
import ActivateAccount from '@/domain/auth/ActivateAccount';
import ResetPassword from '@/domain/auth/ResetPassword';
import SignIn from '@/domain/auth/SignIn';
import SignUp from '@/domain/auth/SignUp';
import AccountStatus from '@/domain/account-status/AccountStatus';
import AcquisitionLinks from '@/domain/acquisition/AcquisitionLinks';
import EventDetail from '@/domain/events/EventDetail';
import EventForm from '@/domain/events/EventForm';
import EventsList from '@/domain/events/EventsList';
import Integration from '@/domain/integration/Integration';
import PartnerInvite from '@/domain/invite/PartnerInvite';
import OfferDetail from '@/domain/offers/OfferDetail';
import OfferForm from '@/domain/offers/OfferForm';
import OffersList from '@/domain/offers/OffersList';
import Onboarding from '@/domain/onboarding/Onboarding';
import Results from '@/domain/results/Results';
import { useSessionLogoutRedirect } from '@/hooks/useSessionLogoutRedirect';
import { ScreenRoutes } from '@/types/router';

/** Signed-in pages: resolve the session first and send anyone without one to sign-in. */
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { data: user, isLoading, isError } = useMe();
  useSessionLogoutRedirect();

  if (isLoading) return <PageLoader />;
  if (isError || !user) return <Navigate to={ScreenRoutes.SIGNIN} replace />;

  return (
    <ErrorBoundary>
      <PartnerTemplate>{children}</PartnerTemplate>
    </ErrorBoundary>
  );
};

/** Sign-in and sign-up: a signed-in user goes straight to the portal. */
const GuestOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { data: user, isLoading } = useMe();

  if (isLoading) return <PageLoader />;
  if (user) return <Navigate to={ScreenRoutes.HOME} replace />;

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/** Links opened from e-mails work with or without a session. */
const PublicRoute = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary>{children}</ErrorBoundary>
);

/** Route registration, same idea as bodyprint-web: public, guest-only and protected routes. */
const routes: RouteObject[] = [
  {
    path: ScreenRoutes.SIGNIN,
    element: (
      <GuestOnlyRoute>
        <SignIn />
      </GuestOnlyRoute>
    ),
  },
  {
    path: ScreenRoutes.SIGNUP,
    element: (
      <GuestOnlyRoute>
        <SignUp />
      </GuestOnlyRoute>
    ),
  },
  {
    path: ScreenRoutes.ACTIVATE_ACCOUNT,
    element: (
      <PublicRoute>
        <ActivateAccount />
      </PublicRoute>
    ),
  },
  {
    path: ScreenRoutes.RESET_PASSWORD,
    element: (
      <PublicRoute>
        <ResetPassword />
      </PublicRoute>
    ),
  },
  {
    path: ScreenRoutes.INVITE,
    element: (
      <PublicRoute>
        <PartnerInvite />
      </PublicRoute>
    ),
  },
  { path: ScreenRoutes.HOME, element: <Navigate to={ScreenRoutes.EVENTS} replace /> },
  {
    path: ScreenRoutes.EVENTS,
    element: (
      <ProtectedRoute>
        <AccountStatus showPendingNotice>{() => <EventsList />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.EVENT_NEW,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <EventForm partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.EVENT_DETAIL,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <EventDetail partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.EVENT_EDIT,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <EventForm partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.OFFERS,
    element: (
      <ProtectedRoute>
        <AccountStatus showPendingNotice>{() => <OffersList />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.OFFER_NEW,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <OfferForm partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.OFFER_DETAIL,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <OfferDetail partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.OFFER_EDIT,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <OfferForm partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.RESULTS,
    element: (
      <ProtectedRoute>
        <AccountStatus showPendingNotice>{() => <Results />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.LINKS,
    element: (
      <ProtectedRoute>
        <AccountStatus showPendingNotice>{() => <AcquisitionLinks />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.INTEGRATION,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <Integration partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.ACCOUNT,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <Account partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.ACCOUNT_EDIT,
    element: (
      <ProtectedRoute>
        <AccountStatus>{(partner) => <AccountEdit partner={partner} />}</AccountStatus>
      </ProtectedRoute>
    ),
  },
  {
    path: ScreenRoutes.ONBOARDING,
    element: (
      <ProtectedRoute>
        <Onboarding />
      </ProtectedRoute>
    ),
  },
  { path: '*', element: <ErrorBoundaryFallback /> },
];

export const router = createBrowserRouter(
  routes.map((route) => ({ ...route, errorElement: <ErrorBoundaryFallback /> })),
);
